/**
 * SiteEquipmentPicker — Iter 397e.
 *
 * Customer > Equipment tab now enforces the site hierarchy. Instead
 * of a flat list of every equipment across every site, show a card
 * per site with:
 *   • name
 *   • equipment count
 *   • next-service info (soonest upcoming or "overdue by N days")
 *   • click-through into the site's own Equipment tab where all the
 *     drill-down features (add/edit interval, service history,
 *     scheduler) already exist.
 *
 * Add-equipment lives on the site page, not here — you can't add
 * equipment without knowing which site it belongs to.
 */
import { Link } from "react-router-dom";
import { Cog, MapPin, ArrowRight, CalendarClock, AlertTriangle } from "lucide-react";

export default function SiteEquipmentPicker({ sites, equipment, serviceLogs, customerId }) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const perSite = (sites || []).map((s) => {
    const items = (equipment || []).filter((e) => e.site_id === s.id);
    const withDue = items
      .map((e) => e.next_service_date ? new Date(e.next_service_date) : null)
      .filter(Boolean)
      .sort((a, b) => a - b);
    const soonest = withDue[0] || null;
    let dueLabel = null;
    let dueTone = "text-[#647087]";
    if (soonest) {
      const diffDays = Math.round((soonest - now) / 86400000);
      if (diffDays < 0) {
        dueLabel = `${Math.abs(diffDays)}d overdue`;
        dueTone = "text-rose-300";
      } else if (diffDays === 0) {
        dueLabel = "Due today";
        dueTone = "text-amber-300";
      } else if (diffDays <= 7) {
        dueLabel = `Due in ${diffDays}d`;
        dueTone = "text-amber-300";
      } else {
        dueLabel = `Next: ${soonest.toLocaleDateString([], { day: "numeric", month: "short" })}`;
        dueTone = "text-cyan-300";
      }
    }
    return { site: s, count: items.length, dueLabel, dueTone };
  }).sort((a, b) => b.count - a.count);

  const totalEquipment = (equipment || []).length;

  if (sites.length === 0) {
    return (
      <div className="hv-card p-6 text-center" data-testid="site-eq-picker-empty">
        <MapPin className="w-8 h-8 text-[#647087] mx-auto mb-2" />
        <div className="text-sm text-white font-medium">No sites yet</div>
        <div className="text-xs text-[#a0aabf] mt-1">
          Add a site to this customer before you can track equipment. Sites live under the customer overview.
        </div>
      </div>
    );
  }

  return (
    <div className="hv-card !p-0 overflow-hidden" data-testid="site-eq-picker">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-white/[0.02]">
        <div className="w-8 h-8 rounded-md bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
          <Cog className="w-4 h-4 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.15em] text-emerald-300 font-bold">Equipment</div>
          <div className="text-[11px] text-[#a0aabf]">
            Select a site to view or add equipment · {totalEquipment} item{totalEquipment === 1 ? "" : "s"} across {sites.length} site{sites.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <div className="divide-y divide-white/5">
        {perSite.map(({ site, count, dueLabel, dueTone }) => (
          <Link
            key={site.id}
            to={`/customers/${customerId}/sites/${site.id}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition group"
            data-testid={`site-eq-picker-row-${site.id}`}
          >
            <div className="w-8 h-8 rounded-md bg-cyan-500/10 border border-cyan-400/25 flex items-center justify-center shrink-0 group-hover:border-cyan-400/50 transition">
              <MapPin className="w-3.5 h-3.5 text-cyan-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate font-medium">{site.name || "Unnamed site"}</div>
              {site.address && (
                <div className="text-[10px] text-[#647087] truncate">{site.address}</div>
              )}
            </div>
            <div className="flex flex-col items-end gap-0.5 shrink-0 min-w-[80px]">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-mono text-white font-bold" data-testid={`site-eq-picker-count-${site.id}`}>
                  {count}
                </span>
                <Cog className={`w-3 h-3 ${count === 0 ? "text-[#647087]" : "text-emerald-300"}`} />
              </div>
              {dueLabel && (
                <div className={`flex items-center gap-1 text-[10px] ${dueTone}`}>
                  {dueTone.includes("rose") ? <AlertTriangle className="w-2.5 h-2.5" /> : <CalendarClock className="w-2.5 h-2.5" />}
                  <span className="font-mono">{dueLabel}</span>
                </div>
              )}
            </div>
            <ArrowRight className="w-4 h-4 text-[#647087] group-hover:text-cyan-300 transition shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
